import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import type { ChatFolder, FolderColor, Locale } from "@mazzager/contracts";
import { ArrowDown, ArrowUp, Check, Folder, Plus, Trash2, X } from "lucide-react";
import { api } from "../lib/api";

type FolderConversation = { id: string; title: string };

const colors: Array<{ id: FolderColor; value: string }> = [
  { id: "green", value: "#21df79" }, { id: "teal", value: "#20c7a5" },
  { id: "cyan", value: "#28b9d8" }, { id: "blue", value: "#4388ee" },
  { id: "indigo", value: "#6574e8" }, { id: "violet", value: "#9a72e8" },
  { id: "pink", value: "#dd6bb5" }, { id: "red", value: "#e76565" },
  { id: "orange", value: "#e88d4d" }, { id: "amber", value: "#ddb43d" },
];

export const folderColor = (color: FolderColor) => colors.find((item) => item.id === color)?.value ?? "#21df79";

export function FolderManager({ locale, demo, folders, conversations, closing, onClose, onFoldersChange }: {
  locale: Locale;
  demo: boolean;
  folders: ChatFolder[];
  conversations: FolderConversation[];
  closing: boolean;
  onClose: () => void;
  onFoldersChange: (folders: ChatFolder[]) => void;
}) {
  const [selectedId, setSelectedId] = useState(folders[0]?.id ?? "");
  const [creating, setCreating] = useState(false);
  const selected = useMemo(() => folders.find((folder) => folder.id === selectedId), [folders, selectedId]);
  const [name, setName] = useState("");
  const [color, setColor] = useState<FolderColor>("green");
  const [conversationIds, setConversationIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (creating) return;
    const folder = selected ?? folders[0];
    if (!folder) return;
    if (!selected) setSelectedId(folder.id);
    setName(folder.name);
    setColor(folder.color);
    setConversationIds(folder.conversationIds);
    setError("");
  }, [creating, folders, selected]);

  const choose = (folder: ChatFolder) => {
    setCreating(false);
    setSelectedId(folder.id);
    setName(folder.name);
    setColor(folder.color);
    setConversationIds(folder.conversationIds);
    setError("");
  };

  const startCreating = () => {
    setCreating(true);
    setSelectedId("");
    setName("");
    setColor("green");
    setConversationIds([]);
    setError("");
  };

  const commitFolders = (next: ChatFolder[]) => onFoldersChange([...next].sort((a, b) => a.position - b.position));

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true); setError("");
    try {
      const payload = { name: trimmed, color, ...((selected?.isAll) ? {} : { conversationIds }) };
      let folder: ChatFolder;
      if (demo) {
        folder = creating
          ? { id: `demo-folder-${crypto.randomUUID()}`, name: trimmed, color, position: folders.length, isAll: false, conversationIds }
          : { ...selected!, ...payload, conversationIds: selected?.isAll ? [] : conversationIds };
      } else {
        const result = await api.request<{ folder: ChatFolder }>(creating ? "/chat-folders" : `/chat-folders/${selected!.id}`, {
          method: creating ? "POST" : "PATCH",
          body: JSON.stringify(payload),
        });
        folder = result.folder;
      }
      const next = creating ? [...folders, folder] : folders.map((item) => item.id === folder.id ? folder : item);
      commitFolders(next);
      setCreating(false);
      setSelectedId(folder.id);
    } catch (reason) {
      setError(reason instanceof Error && reason.message.trim() ? reason.message : (locale === "ru" ? "Не удалось сохранить папку" : "Could not save folder"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!selected || selected.isAll) return;
    setBusy(true); setError("");
    try {
      if (!demo) await api.request(`/chat-folders/${selected.id}`, { method: "DELETE" });
      const next = folders.filter((folder) => folder.id !== selected.id);
      commitFolders(next);
      if (next[0]) choose(next[0]);
    } catch (reason) {
      setError(reason instanceof Error && reason.message.trim() ? reason.message : (locale === "ru" ? "Не удалось удалить папку" : "Could not delete folder"));
    } finally { setBusy(false); }
  };

  const move = async (direction: -1 | 1) => {
    if (!selected || selected.isAll) return;
    const ordered = [...folders].sort((a, b) => a.position - b.position);
    const index = ordered.findIndex((folder) => folder.id === selected.id);
    const target = index + direction;
    if (target < 1 || target >= ordered.length) return;
    const currentFolder = ordered[index];
    const targetFolder = ordered[target];
    if (!currentFolder || !targetFolder) return;
    ordered[index] = targetFolder;
    ordered[target] = currentFolder;
    const optimistic = ordered.map((folder, position) => ({ ...folder, position }));
    commitFolders(optimistic);
    try {
      if (!demo) {
        const result = await api.request<{ folders: ChatFolder[] }>("/chat-folders/order", { method: "PUT", body: JSON.stringify({ folderIds: optimistic.map((folder) => folder.id) }) });
        commitFolders(result.folders);
      }
    } catch (reason) {
      commitFolders(folders);
      setError(reason instanceof Error && reason.message.trim() ? reason.message : (locale === "ru" ? "Не удалось изменить порядок" : "Could not reorder folders"));
    }
  };

  const activeIsAll = !creating && Boolean(selected?.isAll);
  return <div className={`folder-manager-backdrop ${closing ? "closing" : ""}`} onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
    <section className="folder-manager" role="dialog" aria-modal="true" aria-labelledby="folder-manager-title">
      <header><div><Folder /><span><h2 id="folder-manager-title">{locale === "ru" ? "Папки чатов" : "Chat folders"}</h2><p>{locale === "ru" ? "Папки синхронизируются на всех устройствах" : "Folders sync across your devices"}</p></span></div><button type="button" aria-label={locale === "ru" ? "Закрыть" : "Close"} onClick={onClose} disabled={busy}><X /></button></header>
      <div className="folder-manager-body">
        <aside>
          <button type="button" className="folder-add" onClick={startCreating} disabled={folders.length >= 13}><Plus />{locale === "ru" ? "Новая папка" : "New folder"}</button>
          <div>{folders.map((folder) => <button type="button" key={folder.id} className={!creating && selectedId === folder.id ? "active" : ""} onClick={() => choose(folder)} style={{ "--folder-color": folderColor(folder.color) } as CSSProperties}><i /><span>{folder.name}</span><em>{folder.isAll ? conversations.length : folder.conversationIds.length}</em></button>)}</div>
        </aside>
        <main>
          <label><span>{locale === "ru" ? "Название" : "Name"}</span><input autoFocus={creating} maxLength={24} value={name} onChange={(event) => setName(event.target.value)} placeholder={locale === "ru" ? "Название папки" : "Folder name"} /></label>
          <div className="folder-colors" aria-label={locale === "ru" ? "Цвет папки" : "Folder color"}>{colors.map((item) => <button type="button" key={item.id} aria-label={item.id} aria-pressed={color === item.id} style={{ "--folder-color": item.value } as CSSProperties} onClick={() => setColor(item.id)}>{color === item.id && <Check />}</button>)}</div>
          {!activeIsAll && <fieldset><legend>{locale === "ru" ? "Чаты в папке" : "Chats in folder"}</legend><div className="folder-chat-list">{conversations.length ? conversations.map((conversation) => { const checked = conversationIds.includes(conversation.id); return <label key={conversation.id}><input type="checkbox" checked={checked} onChange={() => setConversationIds((current) => checked ? current.filter((id) => id !== conversation.id) : [...current, conversation.id])} /><span>{conversation.title}</span></label>; }) : <p>{locale === "ru" ? "Сначала создайте чат" : "Create a chat first"}</p>}</div></fieldset>}
          {activeIsAll && <div className="folder-all-note">{locale === "ru" ? "Все чаты — обязательная папка. Её можно переименовать, но нельзя удалить." : "All chats is required. You can rename it, but it cannot be deleted."}</div>}
          {error && <div className="form-error" role="alert">{error}</div>}
          <footer>
            {!creating && selected && !selected.isAll && <><button type="button" className="folder-delete" onClick={() => void remove()} disabled={busy}><Trash2 />{locale === "ru" ? "Удалить" : "Delete"}</button><div className="folder-order"><button type="button" aria-label={locale === "ru" ? "Выше" : "Move up"} onClick={() => void move(-1)} disabled={busy}><ArrowUp /></button><button type="button" aria-label={locale === "ru" ? "Ниже" : "Move down"} onClick={() => void move(1)} disabled={busy}><ArrowDown /></button></div></>}
            <button type="button" className="folder-save" onClick={() => void save()} disabled={busy || !name.trim()}>{busy ? <span className="spinner" /> : <Check />}{locale === "ru" ? "Сохранить" : "Save"}</button>
          </footer>
        </main>
      </div>
    </section>
  </div>;
}
